<template>
  <section class="form-list">
    <el-form
      ref="form"
      :rules="rules"
      :model="postData"
      label-position="right"
      label-width="160px"
      class="form"
    >
      <el-form-item label="活动 ID">{{ id }}</el-form-item>

      <el-form-item label="活动名称" prop="activity_name">
        <el-input
          v-model="postData.activity_name"
          maxlength="16"
          show-word-limit
          clearable
          placeholder="请输入活动名称"
        />
      </el-form-item>

      <el-form-item label="统计开始时间" prop="start_time">
        <el-date-picker
          class="w100"
          v-model="postData.start_time"
          type="datetime"
          value-format="timestamp"
          :picker-options="startTimePickerOptions"
          :disabled="isIssued"
          placeholder="请选择统计开始时间"
        />
      </el-form-item>

      <el-form-item label="统计结束时间" prop="end_time">
        <el-date-picker
          class="w100"
          v-model="postData.end_time"
          type="datetime"
          value-format="timestamp"
          :picker-options="endTimePickerOptions"
          :disabled="isIssued"
          placeholder="请选择统计结束时间"
          @change="onEndTimeChange"
        />
      </el-form-item>

      <el-alert
        v-if="isIssued"
        title="该活动已发放代金券，统计区间与等级券类型不可修改，仅可修改活动名称与小程序展示状态。"
        type="warning"
        show-icon
        :closable="false"
        style="margin-bottom: 16px;"
      />

      <el-form-item label="小程序展示">
        <el-switch
          v-model="postData.is_show"
          :active-value="1"
          :inactive-value="0"
          active-text="展示"
          inactive-text="不展示"
        />
      </el-form-item>

      <el-form-item
        v-for="level in levels"
        :key="level.id"
        :label="`${level.level_name}等级券类型`"
      >
        <el-select
          v-model="postData.voucher_type_ids[level.id]"
          class="w100"
          filterable
          clearable
          :disabled="isIssued"
          placeholder="不选择代金券，该等级不发放"
        >
          <el-option
            v-for="voucherType in voucherTypes"
            :key="voucherType.id"
            :label="`${voucherType.voucher_type_name}，${toYuan(voucherType.amount_in_cents)} 元`"
            :value="voucherType.id"
          />
        </el-select>
        <el-alert
          title="不选择代金券，代表该等级不发放代金券。"
          type="info"
          show-icon
          :closable="false"
        />
      </el-form-item>

      <el-form-item>
        <el-button type="primary" :loading="isSaving" :disabled="isLoading" @click="handleSubmit">提交</el-button>
        <el-button @click="RouteConfig.studentRankActivityList.back">取消</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { voucherIssueTypes } from '@/enums/voucherIssueTypes';
import { show, update } from '@/api/student-rank-activity';
import { index as indexLevels } from '@/api/student-rank-level';
import { index as indexVoucherTypes } from '@/api/voucher-type';
import { toMillisecond, toSecond, toYuan } from '@/filters';

export default {
  name: RouteConfig.studentRankActivityEdit.name,
  data: () => ({
    RouteConfig,
    id: 0,
    update_time: 0,
    issued_count: 0,
    isLoading: true,
    isSaving: false,
    levels: [],
    voucherTypes: [],
    postData: {
      activity_name: '',
      start_time: '',
      end_time: '',
      is_show: 1,
      voucher_type_ids: {},
    },
  }),
  computed: {
    isIssued() {
      return this.issued_count > 0;
    },
    rules() {
      return {
        activity_name: [{ required: true, message: '请输入活动名称', trigger: 'change' }],
        start_time: [{ required: true, message: '请选择统计开始时间', trigger: 'change' }],
        end_time: [{
          validator: (rule, value, callback) => {
            if (!value) return callback(new Error('请选择统计结束时间'));
            callback(value > this.postData.start_time ? undefined : new Error('结束时间必须大于开始时间'));
          },
          trigger: 'change',
        }],
      };
    },
    startTimePickerOptions() {
      return {
        disabledDate: (time) => {
          if (!this.postData.end_time) return false;
          return time.getTime() > new Date(this.postData.end_time).getTime();
        },
      };
    },
    endTimePickerOptions() {
      const startMs = this.postData.start_time;
      if (!startMs) return { disabledDate: () => false };

      const startDate = new Date(startMs);
      startDate.setHours(0, 0, 0, 0);
      return {
        disabledDate: (time) => time.getTime() < startDate.getTime(),
      };
    },
  },
  methods: {
    toYuan,
    async load() {
      this.id = +this.$route.params.id || 0;
      if (!this.id) return RouteConfig.studentRankActivityList.push();

      this.isLoading = true;
      const [levelsResult, voucherTypesResult, detailResult] = await Promise.all([
        indexLevels({ is_del: 0, size: 200 }),
        indexVoucherTypes({ is_disable_count: 1, issue_types: [voucherIssueTypes.activity.value] }),
        show(this.id),
      ]);
      this.isLoading = false;

      if (!levelsResult.isError) this.levels = levelsResult.data.list || [];
      if (!voucherTypesResult.isError) this.voucherTypes = voucherTypesResult.data.list || [];

      const { isError, data } = detailResult;
      if (isError || !data || !data.id) return RouteConfig.studentRankActivityList.push();

      this.update_time = data.update_time || 0;
      this.issued_count = data.issued_count || 0;

      this.levels.forEach((level) => {
        this.$set(this.postData.voucher_type_ids, level.id, '');
      });
      (data.levels || []).forEach((level) => {
        this.$set(this.postData.voucher_type_ids, level.level_id, level.voucher_type_id || '');
      });

      this.postData.activity_name = data.activity_name || '';
      this.postData.start_time = data.start_time ? toMillisecond(data.start_time) : '';
      this.postData.end_time = data.end_time ? toMillisecond(data.end_time) : '';
      this.postData.is_show = data.is_show ? 1 : 0;
    },
    onEndTimeChange(value) {
      if (!value || !this.postData.start_time) return;
      if (+value <= +this.postData.start_time) {
        this.postData.end_time = +this.postData.start_time + 1000;
      }
    },
    async handleSubmit() {
      if (this.isSaving) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        this.isSaving = true;
        const { isError } = await update(this.id, {
          update_time: this.update_time,
          activity_name: this.postData.activity_name,
          start_time: toSecond(this.postData.start_time),
          end_time: toSecond(this.postData.end_time),
          is_show: this.postData.is_show,
          voucher_type_ids: this.postData.voucher_type_ids,
        });
        this.isSaving = false;
        if (!isError) RouteConfig.studentRankActivityList.push();
      });
    },
  },
  beforeMount() {
    this.load();
  },
};
</script>
